import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FacebookShareButton } from 'react-share';

import Button from '../../../../../../components/global/Button';
import { getAsset } from '../../../../../../util/assets';
import NativeActivityIndicator from '../../../../../../components/global/NativeActivityIndicator';
import useAlbumFolderMarketingAssets from '../../../../views/AlbumFolder/AlbumFolderMarketing/components/AlbumFolderMarketingList/useAlbumFolderMarketingAssets';
import AssetDownloadButton from '../../../../views/AlbumFolder/AlbumFolderMarketing/components/AssetDownloadButton';
import FacebookSquareIcon from '../../../../../../components/icons/FacebookSquareIcon';
import JPGIcon from '../../../../../../components/icons/JPGIcon';
import PDFIcon from '../../../../../../components/icons/PDFIcon';
import { MARKETING_MATERIAL_SAMPLE_MODAL } from '../MarketingMaterialSample';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import { removeHashes } from '../../../../../../util/text';
import { marketingMaterialRequiresConfiguration } from './helpers';
import {
  MarketingMaterialConfiguratorModal,
  MARKETING_MATERIAL_CONFIGURATION_MODAL,
} from '../MarketingMaterialConfigurator';
import useCloseModal from '../../../../../../hooks/useCloseModal';

const propTypes = {
  entity: PropTypes.shape({
    hash_code: PropTypes.string,
  }),
  albumFolderId: PropTypes.string.isRequired,
  isOwner: PropTypes.bool,
  shareable: PropTypes.bool,
};

const MarketingMaterials = ({
  entity,
  albumFolderId,
  isOwner,
  shareable = true,
}) => {
  const openModal = useOpenModal();
  const closeModal = useCloseModal();

  const {
    albumFolder,
    marketingMaterials,
    onMarketingMaterialsAssetGenerate,
    assetsCreating,
    availableMarketingMaterialTypes,
  } = useAlbumFolderMarketingAssets({
    albumFolderId,
  });

  return (
    <div className="shutterbug-share-marketing-material-list mt-2">
      {availableMarketingMaterialTypes.map(marketingMaterialTypeItem => {
        const marketingMaterial = marketingMaterials.find(
          x => x.type === marketingMaterialTypeItem.type,
        );
        // Since being VGB does not mean that all content will have VGB specific previews
        // we will default to common preview in lieu of VGB preview to future proof
        const previewUrl =
          marketingMaterial?.image_url ??
          getAsset(
            albumFolder?.has_video_album
              ? (marketingMaterialTypeItem.vgb_preview ??
                  (marketingMaterialTypeItem.getPreview?.(albumFolder) ||
                    marketingMaterialTypeItem.preview))
              : marketingMaterialTypeItem.getPreview?.(albumFolder) ||
                  marketingMaterialTypeItem.preview,
          );
        const isLoading = !!assetsCreating?.length;
        const isGenerating = assetsCreating.includes(marketingMaterial?.id);

        const cardClasses = classNames({
          'share-marketing-material-card': true,
          'is-loading': isLoading,
          'is-generating': isGenerating,
        });

        return (
          <div className={cardClasses} key={marketingMaterialTypeItem.type}>
            <div className="d-flex">
              {}
              <div
                role="button"
                tabIndex={0}
                onClick={() =>
                  openModal(MARKETING_MATERIAL_SAMPLE_MODAL, {
                    previewUrl,
                    label: marketingMaterialTypeItem.label,
                  })
                }
              >
                <figure className="share-marketing-material-preview">
                  <img src={previewUrl} alt="" />

                  {isGenerating && <NativeActivityIndicator />}
                  <p className="text-xs text-light text-center  mb-0 mt-auto">
                    {marketingMaterial?.image_url
                      ? 'View preview'
                      : 'View sample'}
                  </p>
                </figure>
              </div>
              <div className="ml-2">
                <h4 className={marketingMaterial?.image_url ? 'mb-0' : 'mb-2'}>
                  {marketingMaterialTypeItem.label}
                </h4>

                {isOwner && (
                  <Button
                    disabled={isLoading}
                    onClick={() => {
                      if (
                        marketingMaterialRequiresConfiguration(
                          marketingMaterialTypeItem.type,
                        )
                      ) {
                        openModal(MARKETING_MATERIAL_CONFIGURATION_MODAL, {
                          marketingMaterialType: marketingMaterialTypeItem.type,
                          albumFolder,
                          label: marketingMaterialTypeItem.label,
                          defaultConfiguration:
                            marketingMaterial?.configuration,
                        });
                      } else {
                        onMarketingMaterialsAssetGenerate({
                          type: marketingMaterialTypeItem.type,
                        });
                      }
                    }}
                    btnOutline={!marketingMaterial?.image_url}
                    className="text-sm text-extra-bold mb-2"
                    btnSize="sm"
                    btnStyle={marketingMaterial?.image_url ? 'link' : undefined}
                  >
                    {(() => {
                      if (isGenerating) {
                        return 'Creating...';
                      }

                      return marketingMaterial?.image_url
                        ? 'Regenerate'
                        : 'Generate';
                    })()}
                  </Button>
                )}
                {marketingMaterial?.pdf_url && marketingMaterial?.image_url && (
                  <div className="d-flex">
                    <AssetDownloadButton
                      disabled={isGenerating}
                      className="shutterbug-share-action-button mr-1"
                      btnStyle="reset"
                      accountMarketingMaterialId={marketingMaterial?.id}
                      assetKey="pdf_download_url"
                      icon={
                        <div>
                          <PDFIcon color="#0d84fe" size={20} />
                        </div>
                      }
                      label="PDF"
                    />
                    <AssetDownloadButton
                      disabled={isGenerating}
                      className="shutterbug-share-action-button mr-1"
                      btnStyle="reset"
                      accountMarketingMaterialId={marketingMaterial?.id}
                      assetKey="image_download_url"
                      icon={
                        <div>
                          <JPGIcon color="#0d84fe" size={20} />
                        </div>
                      }
                      label="JPG"
                    />
                    {shareable && (
                      <FacebookShareButton
                        url={marketingMaterial.image_url}
                        className="shutterbug-share-action-button mr-1"
                        hashtag={
                          entity?.hash_code &&
                          `#${removeHashes(entity?.hash_code)}`
                        }
                      >
                        <div>
                          <FacebookSquareIcon color="#0d84fe" size={20} />
                        </div>
                        Facebook
                      </FacebookShareButton>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      <MarketingMaterialConfiguratorModal
        onConfigurationComplete={({ marketingMaterialType, configuration }) => {
          closeModal(MARKETING_MATERIAL_CONFIGURATION_MODAL);

          onMarketingMaterialsAssetGenerate({
            type: marketingMaterialType,
            configuration,
          });
        }}
      />
    </div>
  );
};

MarketingMaterials.propTypes = propTypes;

export default MarketingMaterials;
